import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Rating,
  Divider,
} from '@mui/material';
import {
  RateReview as RateReviewIcon,
  Star as StarIcon,
  ThumbUp as ThumbUpIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { feedbackService } from '../../services/feedback.service';
import { AdminFeedbackOverview } from '../../types/feedback.types';
import { useNotification } from '../../context/NotificationContext';

const statCards = [
  { key: 'totalFeedback' as const, label: 'Total Feedback', icon: RateReviewIcon, color: '#3b82f6' },
  { key: 'averageRating' as const, label: 'Average Rating', icon: StarIcon, color: '#f59e0b' },
  { key: 'recommendRate' as const, label: 'Recommend Rate', icon: ThumbUpIcon, color: '#22c55e' },
  { key: 'thisWeek' as const, label: 'This Week', icon: TrendingUpIcon, color: '#8b5cf6' },
];

const getRatingColor = (rating: number) => {
  if (rating >= 4.5) return '#22c55e';
  if (rating >= 3.5) return '#3b82f6';
  return '#f59e0b';
};

const AdminFeedback: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [overview, setOverview] = useState<AdminFeedbackOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    feedbackService.getAdminOverview()
      .then(setOverview)
      .catch(() => showNotification('Failed to load feedback data', 'error'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExportCsv = () => {
    if (!overview || overview.recentFeedback.length === 0) return;

    const headers = ['Student', 'Workshop', 'Session', 'Date', 'Overall', 'Content', 'HandsOn', 'Environment', 'Difficulty', 'Recommend', 'WorkedWell', 'CouldImprove'];
    const rows = overview.recentFeedback.map((f) => [
      f.studentName,
      f.workshopTitle,
      f.sessionName,
      dayjs(f.submittedAt).format('YYYY-MM-DD'),
      String(f.overallRating),
      String(f.contentClarityRating),
      String(f.handsOnRelevanceRating),
      String(f.environmentQualityRating),
      f.difficultyPerception || '',
      f.recommendWorkshop === null ? '' : f.recommendWorkshop ? 'Yes' : 'No',
      (f.whatWorkedWell || '').replace(/"/g, '""'),
      (f.whatCouldImprove || '').replace(/"/g, '""'),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `feedback-export-${dayjs().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!overview) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">Failed to load feedback data.</Typography>
      </Box>
    );
  }

  // Compute stat values
  const thisWeekCount = overview.thisWeekCount;

  const statValues = {
    totalFeedback: String(overview.totalFeedbackCount),
    averageRating: overview.averageOverallRating !== null ? `${overview.averageOverallRating}/5` : '--',
    recommendRate: overview.averageRecommendationRate !== null ? `${overview.averageRecommendationRate}%` : '--',
    thisWeek: String(thisWeekCount),
  };

  // Empty state
  if (overview.totalFeedbackCount === 0) {
    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Feedback & Ratings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Platform-wide student feedback overview
        </Typography>
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: '12px' }} elevation={0}>
          <RateReviewIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No feedback submitted yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Feedback will appear here once students complete sessions and submit their reviews.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
            Feedback & Ratings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Platform-wide student feedback overview
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExportCsv}
          disabled={overview.recentFeedback.length === 0}
        >
          Export CSV
        </Button>
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {statCards.map((card) => {
          const Icon = card.icon;
          const value = statValues[card.key];
          return (
            <Grid size={{ xs: 6, md: 3 }} key={card.key}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: '12px',
                  borderLeft: `4px solid ${card.color}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
                elevation={0}
              >
                <Avatar
                  sx={{
                    bgcolor: `${card.color}15`,
                    color: card.color,
                    width: 48,
                    height: 48,
                  }}
                >
                  <Icon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {card.label}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Session Rankings */}
      {overview.sessionRankings.length > 0 && (
        <Paper sx={{ p: 2.5, borderRadius: '12px', mb: 3 }} elevation={0}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Top Rated Sessions
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', width: 40 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>Workshop / Session</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>Avg Rating</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', textAlign: 'right' }}>Responses</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview.sessionRankings.slice(0, 10).map((ranking, idx) => (
                  <TableRow
                    key={ranking.sessionId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/instructor/session/${ranking.sessionId}/summary`)}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {idx + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {ranking.workshopTitle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ranking.sessionName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Rating value={ranking.averageRating} precision={0.1} readOnly size="small" />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: getRatingColor(ranking.averageRating) }}
                        >
                          {ranking.averageRating}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {ranking.feedbackCount}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Recent Feedback */}
      <Paper sx={{ p: 2.5, borderRadius: '12px' }} elevation={0}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          All Feedback
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {overview.recentFeedback.map((feedback) => (
            <Paper key={feedback.id} variant="outlined" sx={{ p: 2.5, borderRadius: '8px' }}>
              {/* Header: student name + session info */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {feedback.studentName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {feedback.workshopTitle} / {feedback.sessionName}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {dayjs(feedback.submittedAt).format('MMM D, YYYY')}
                </Typography>
              </Box>

              {/* Ratings row */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1.5 }}>
                {[
                  { label: 'Overall', value: feedback.overallRating },
                  { label: 'Content', value: feedback.contentClarityRating },
                  { label: 'Hands-on', value: feedback.handsOnRelevanceRating },
                  { label: 'Environment', value: feedback.environmentQualityRating },
                ].map((r) => (
                  <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                      {r.label}:
                    </Typography>
                    <Rating value={r.value} readOnly size="small" />
                  </Box>
                ))}
              </Box>

              {/* Difficulty + Recommendation */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                {feedback.difficultyPerception && (
                  <Chip
                    label={feedback.difficultyPerception.replace(/_/g, ' ')}
                    size="small"
                    sx={{ textTransform: 'capitalize', height: 24, fontSize: '0.7rem' }}
                  />
                )}
                {feedback.recommendWorkshop !== null && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {feedback.recommendWorkshop ? (
                      <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : (
                      <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {feedback.recommendWorkshop ? 'Recommends' : 'Does not recommend'}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Comments */}
              {(feedback.whatWorkedWell || feedback.whatCouldImprove) && (
                <>
                  <Divider sx={{ my: 1 }} />
                  {feedback.whatWorkedWell && (
                    <Typography variant="body2" sx={{ mb: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                      &#10003; &ldquo;{feedback.whatWorkedWell}&rdquo;
                    </Typography>
                  )}
                  {feedback.whatCouldImprove && (
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                      &#9651; &ldquo;{feedback.whatCouldImprove}&rdquo;
                    </Typography>
                  )}
                </>
              )}
            </Paper>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminFeedback;
